import React, { useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  lastAssistantMessageIsCompleteWithToolCalls,
} from "ai";
import { Bot, Send, Broom, MessageSquare, X } from "lucide-react";
import { formatCurrency, formatPercentage, t } from "@/i18n";
import { useMe } from "@/hooks/users/useMe";
import { cn } from "@/components/utils";
import LoadingPanel from "@/components/loadingPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ProgressCircle from "../progressCircle";
import Tooltip from "@/components/tooltip";
import { getAuthToken } from "./chatAuth";
import { AlertButton } from "./ui/alertButton";
import { CollectedUserInput, CollectUserInput } from "./ui/collectUserInput";
import { DisplayItemList } from "./ui/displayItemList";
import { DrawChart } from "./ui/drawChart";
import { MessageResponse } from "./ui/messageResponse";
import { EntityReferenceLink } from "@/components/entityReference/entityReferenceLink";

const formatApprovalLabel = (label) =>
  label
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getApprovalDetails = (value, path = []) => {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return [
      { label: formatApprovalLabel(path.join(" ")), value: value.join(", ") },
    ];
  }

  if (typeof value === "object") {
    return Object.entries(value).flatMap(([key, nestedValue]) =>
      getApprovalDetails(nestedValue, [...path, key]),
    );
  }

  return [{ label: formatApprovalLabel(path.join(" ")), value: String(value) }];
};

const getApprovalTitle = (toolType) => {
  if (toolType === "tool-create_private_shareholder") {
    return "Create private shareholder";
  }

  if (toolType === "tool-create_company_shareholder") {
    return "Create company shareholder";
  }

  return "Approve action";
};

const ToolApproval = ({ part, onRespond }) => {
  const [isResponding, setIsResponding] = useState(false);
  const details = getApprovalDetails(part.input);

  const respond = async (approved) => {
    setIsResponding(true);

    try {
      await onRespond({ id: part.approval.id, approved });
    } catch {
      setIsResponding(false);
    }
  };

  return (
    <div className="min-w-[80%] rounded-lg border bg-slate-100 p-4">
      <h3 className="font-semibold">{getApprovalTitle(part.type)}</h3>
      <p className="text-muted-foreground mt-1">
        Review the details and approve this action.
      </p>
      {details.length > 0 && (
        <dl className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2">
          {details.map((detail) => (
            <React.Fragment key={detail.label}>
              <dt className="text-muted-foreground">{detail.label}</dt>
              <dd className="max-w-64 text-right break-words">
                {detail.value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={isResponding}
          onClick={() => void respond(false)}
        >
          Decline
        </Button>
        <Button
          type="button"
          disabled={isResponding}
          onClick={() => void respond(true)}
        >
          Approve
        </Button>
      </div>
    </div>
  );
};

function renderMessagePart(
  part,
  index,
  role,
  addToolOutput,
  addToolApprovalResponse,
  refocusInput,
) {
  if (part.state === "approval-requested") {
    return (
      <ToolApproval
        key={part.toolCallId ?? index}
        part={part}
        onRespond={addToolApprovalResponse}
      />
    );
  }

  switch (part.type) {
    case "text":
      return (
        <div
          key={index}
          className={cn(
            "p-sm max-w-[80%] rounded-lg",
            role === "user"
              ? "bg-brand-700 text-primary-foreground"
              : "text-foreground bg-slate-100",
          )}
        >
          <MessageResponse className="gap-md grid">{part.text}</MessageResponse>
        </div>
      );
    case "tool-showAlertButton":
      if (part.state !== "output-available") return null;

      return (
        <AlertButton
          key={part.toolCallId ?? index}
          label={part.output.label}
          message={part.output.message}
        />
      );
    case "tool-drawChart":
      if (part.state !== "output-available") return null;

      return (
        <DrawChart
          key={part.toolCallId ?? index}
          title={part.output.title}
          series={part.output.series}
          valueFormat={part.output.valueFormat}
        />
      );
    case "tool-showEntityReferences":
      if (part.state !== "output-available") return null;

      return (
        <div key={part.toolCallId ?? index} className="gap-sm flex flex-wrap">
          {part.output.references.map((entityReference) => (
            <EntityReferenceLink
              key={`${entityReference.type}:${entityReference.id}`}
              entityReference={entityReference}
            />
          ))}
        </div>
      );
    case "tool-displayItemList":
      if (part.state !== "output-available") return null;

      return (
        <DisplayItemList
          key={part.toolCallId ?? index}
          title={part.input.title}
          description={part.input.description}
          emptyText={part.input.emptyText}
          currency={part.input.currency}
          columns={part.input.columns}
          rows={part.input.rows}
        />
      );
    case "tool-collectUserInput":
      if (part.state === "input-available") {
        return (
          <CollectUserInput
            key={part.toolCallId ?? index}
            definition={part.input}
            onSubmit={(values) => {
              addToolOutput({
                tool: "collectUserInput",
                toolCallId: part.toolCallId,
                output: { status: "submitted", values },
              });
              refocusInput?.();
            }}
            onCancel={() => {
              addToolOutput({
                tool: "collectUserInput",
                toolCallId: part.toolCallId,
                output: { status: "cancelled" },
              });
              refocusInput?.();
            }}
          />
        );
      }

      if (part.state === "output-available") {
        return (
          <CollectedUserInput
            key={part.toolCallId ?? index}
            definition={part.input}
            result={part.output}
          />
        );
      }

      return null;
    default:
      return null;
  }
}

const AiChatSession = ({
  onClose,
  companyId,
  initialMessages,
  initialConversationId,
  conversationTitle,
  conversationCostSEK,
  onConversationFinished,
  onClearConversation,
  isCreatingConversation,
  onOpenConversations,
}) => {
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const suppressAutomaticSendRef = useRef(false);
  const conversationIdRef = useRef(initialConversationId);
  const [input, setInput] = useState("");
  const [isResolvingPendingForm, setIsResolvingPendingForm] = useState(false);
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  const [tokenUsagePercent, setTokenUsagePercent] = useState(0);

  const { data: meQuery } = useMe();
  const user = meQuery?.data;

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: "/api/ai-agent/chat",
        headers: () => {
          const currentCompanyId = companyIdRef.current;
          return {
            Authorization: `Bearer ${getAuthToken()}`,
            ...(currentCompanyId ? { "x-company-id": currentCompanyId } : {}),
          };
        },
        body: () => ({
          ...(conversationIdRef.current
            ? { conversationId: conversationIdRef.current }
            : {}),
          // Lets the agent know what the user is looking at. The query string is left out on purpose.
          pageContext: { path: window.location.pathname },
        }),
      }),
    [],
  );

  const {
    messages,
    sendMessage,
    addToolApprovalResponse,
    addToolOutput,
    status,
    error,
  } = useChat({
    transport,
    messages: initialMessages,
    onFinish: ({ message }) => {
      const conversationId =
        message.metadata?.conversationId ?? conversationIdRef.current;
      conversationIdRef.current = conversationId;
      const tokensUsedKey = message.metadata?.tokenUsage?.context?.utilization;

      tokensUsedKey && setTokenUsagePercent(tokensUsedKey);
      if (conversationId) {
        void onConversationFinished?.(conversationId);
      }
    },
    sendAutomaticallyWhen: (options) =>
      !suppressAutomaticSendRef.current &&
      (lastAssistantMessageIsCompleteWithToolCalls(options) ||
        lastAssistantMessageIsCompleteWithApprovalResponses(options)),
  });

  const isLoading = status === "submitted" || status === "streaming";
  const pendingFormToolCalls = useMemo(
    () =>
      messages.flatMap((message) =>
        (message.parts ?? []).filter(
          (part) =>
            part.type === "tool-collectUserInput" &&
            part.state === "input-available",
        ),
      ),
    [messages],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || isLoading || isResolvingPendingForm) return;

    setIsResolvingPendingForm(true);
    suppressAutomaticSendRef.current = true;

    try {
      for (const part of pendingFormToolCalls) {
        await addToolOutput({
          tool: "collectUserInput",
          toolCallId: part.toolCallId,
          output: { status: "cancelled" },
        });
      }

      setInput("");
      await sendMessage({ text });
    } finally {
      suppressAutomaticSendRef.current = false;
      setIsResolvingPendingForm(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const maxHeight = Number.parseFloat(
      window.getComputedStyle(textarea).maxHeight,
    );
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [input]);

  const [isInputFocused, setIsInputFocused] = useState(true);

  useEffect(() => {
    inputRef.current?.focus();

    inputRef.current?.addEventListener("focus", () => {
      setIsInputFocused(true);
      inputRef.current?.scrollIntoView({ behavior: "smooth" });
    });

    inputRef.current?.addEventListener("blur", () => {
      setIsInputFocused(false);
    });
  }, [inputRef?.current]);

  if (!companyId) {
    return <LoadingPanel />;
  }

  return (
    <div className="bg-background relative flex grid h-full w-full flex-col overflow-hidden rounded-xl border shadow-lg">
      <div className="p-md pt-sm from-background via-background absolute top-0 left-0 z-10 flex w-full items-center justify-between bg-linear-to-b to-transparent pb-[20px]">
        <div className="flex items-center justify-between gap-2 text-lg font-semibold">
          <Bot size={24} className="animate-[wiggle_5s_ease-in-out_infinite]" />
          Invono AI{" "}
          <Badge variant="outline-purple" size="sm">
            Beta
          </Badge>
        </div>
        <div className="gap-sm flex items-center">
          <Button
            variant="ghost"
            onClick={onOpenConversations}
            className="rounded-full"
          >
            <MessageSquare size={18} />
            <div className="text-lg">{conversationTitle}</div>
          </Button>
        </div>
        <div>
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onClose}
              title={t("ai_chat.hide_window")}
              aria-label={t("ai_chat.hide_window")}
            >
              <X size={16} />
            </Button>
          )}
        </div>
      </div>
      <div className="p-md gap-md flex h-full flex-1 flex-col overflow-y-auto pt-[50px] pb-[110px]">
        {messages.length === 0 && (
          <div className="text-md gap-md flex h-full w-full flex-col items-center justify-center self-center text-center">
            <Bot
              size={64}
              className="text-brand-500 animate-[wiggle_5s_ease-in-out_infinite]"
            />
            <p>Hi {user?.name} 👋</p>
            <p> I can assist you with managing your work in Invono.</p>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "gap-md text-md flex flex-col",
              m.role === "user" ? "items-end" : "items-start",
            )}
          >
            {m.parts?.map((part, index) =>
              renderMessagePart(
                part,
                index,
                m.role,
                addToolOutput,
                addToolApprovalResponse,
                () => inputRef.current?.focus(),
              ),
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-muted h-10 w-12 rounded-lg">
              <LoadingPanel size={20} />
            </div>
          </div>
        )}
        {error && (
          <p className="text-destructive text-center">{error.message}</p>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form
        onSubmit={handleSubmit}
        className="p-md from-background absolute bottom-0 left-0 flex w-full items-end gap-2 bg-linear-to-t from-[80%] to-transparent pt-[20px]"
      >
        <div
          className={cn(
            "bg-background grid w-full grid-cols-[1fr_auto] gap-2 rounded-xl border",
            isInputFocused &&
              "border-brand-500 shadow-[0_0_10px_var(--color-brand-300)]",
          )}
        >
          <textarea
            autoFocus
            ref={inputRef}
            rows={1}
            className="p-md placeholder:text-muted-foreground max-h-32 w-full flex-1 resize-none overflow-y-auto bg-transparent pr-0 text-lg outline-none"
            placeholder="Type a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInputKeyDown}
          />
          <div className="gap-sm flex items-center">
            <Tooltip value="Clear conversation">
              <button
                type="button"
                onClick={() => void onClearConversation?.()}
                disabled={
                  isLoading || isResolvingPendingForm || isCreatingConversation
                }
                className="text-muted-foreground hover:text-destructive disabled:opacity-40"
              >
                <Broom size={18} />
              </button>
            </Tooltip>
            <Tooltip
              delayDuration={0}
              value={
                <span>
                  <strong>
                    {formatPercentage(tokenUsagePercent, {
                      maximumFractionDigits: 0,
                    })}
                  </strong>{" "}
                  of available context used
                  <br />
                  <strong>{formatCurrency(conversationCostSEK)}</strong> total
                  cost for this conversation
                </span>
              }
            >
              <div className="hover:border-brand-200 group flex items-center gap-2 rounded-full border-2 border-transparent p-2 select-none">
                <span className="text-brand-400 group-hover:text-brand-600 font-semibold">
                  {formatCurrency(conversationCostSEK)}
                </span>
                <ProgressCircle
                  variant="brand"
                  radius={10}
                  max={1}
                  value={tokenUsagePercent}
                  strokeWidth={4}
                />
              </div>
            </Tooltip>
            <button
              type="submit"
              disabled={
                isLoading ||
                isResolvingPendingForm ||
                isCreatingConversation ||
                !input.trim()
              }
              className="text-primary pr-md disabled:opacity-40"
            >
              <Send size={22} />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export { AiChatSession };
